import apiService from './apiService';

/**
 * Telegram day-anchored channel — TWO WORLDS (the Dual-Bucket System).
 *
 * The Library grid is fed by GET /api/telegram/days — one card per DayBlock (a
 * Conversation bucketed to a user-local calendar day), the shared DayChannelPanel
 * contract (deep content search included). `world` selects which
 * chronology to list:
 *
 *   'tools'         → channel='telegram'        — the full agentic PAI
 *   'conversations' → channel='telegram_convo'  — the fast track (light agent)
 *
 * The two are INDEPENDENT stores, so switching worlds is a refetch, not a
 * filter. 'tools' is the BE default, which keeps every pre-split caller working.
 *
 * Rename / Share / Delete of a day reuse the existing conversation endpoints (a
 * DayBlock IS a Conversation) and are therefore world-agnostic.
 *
 * Account linking is a deep-link handshake (Telegram bots can't see phone
 * numbers), and there are two SEPARATE bots to link — one per world — so each
 * has its own mint/unlink pair. Connecting one never affects the other.
 */
export const WORLDS = { TOOLS: 'tools', CONVERSATIONS: 'conversations' };

export const telegramService = {
    // GET /api/telegram/days — Library data source (day_date DESC).
    async listDays({ search = '', limit = 60, offset = 0, world = WORLDS.TOOLS } = {}) {
        const params = new URLSearchParams();
        if (search) params.set('search', search);
        if (limit != null) params.set('limit', String(limit));
        if (offset) params.set('offset', String(offset));
        if (world) params.set('world', world);
        return apiService.get(`/api/telegram/days?${params.toString()}`);
    },

    // Rename a day bucket (User-Defined Rename — title_overridden BE-side).
    async renameDay(dayId, title) {
        return apiService.updateConversation(dayId, { title });
    },

    // Delete a whole DayBlock (messages + voice clips wiped; documents survive).
    async deleteDay(dayId) {
        return apiService.deleteConversation(dayId);
    },

    // ── PAI Tools bot ────────────────────────────────────────────────
    // POST /api/telegram/link → { deep_link, bot_username, expires_in_seconds,
    // linked }. 503 when the bot isn't configured server-side.
    async createLink() {
        return apiService.post('/api/telegram/link', {});
    },

    // DELETE /api/telegram/link → { unlinked: true }.
    async unlink() {
        return apiService.delete('/api/telegram/link');
    },

    // ── PAI Conversations bot (the fast track) ───────────────────────
    // A SECOND BotFather bot with its own token/username, so it has its own
    // link handshake and its own chat id. 503 when it isn't configured.
    async createConvoLink() {
        return apiService.post('/api/telegram/convo/link', {});
    },

    async unlinkConvo() {
        return apiService.delete('/api/telegram/convo/link');
    },
};

export default telegramService;
